/**
 * Deriving an analytics list request's `metaInfo` from the columns a table is
 * configured to show, and mapping the response back onto a row.
 *
 * The analytics list endpoints return entity attributes under `row.metaInfo`,
 * but only the ones the request asked for. Components used to hardcode both
 * halves: a literal metaInfo array in the request and a hand-written line per
 * field when building the row. A column added through org meta then rendered a
 * header and an empty cell, because nothing asked the API for its value and
 * nothing copied it onto the row.
 *
 * Deriving both from the column config closes that gap: configuring a column is
 * enough to make it work.
 */

// Entity attributes each endpoint can serve. Anything else in a table's columns
// is a metric or a computed value and is left alone.
//
// The two endpoints disagree on exactly one key: the alerts API calls the depot
// `originDepot`, the diagnostics API calls it `originDepo`. That is an API
// inconsistency, not a typo here - each list mirrors its own endpoint.
export const SMART_ALERTS_META_INFO_FIELDS = [
  "licensePlateNumber",
  "originCountry",
  "originState",
  "originCity",
  "originDepot",
  "pingStatus",
  "fleetName",
  "fleetCategory",
];

export const BMS_FAULTS_META_INFO_FIELDS = [
  "licensePlateNumber",
  "licensePlateNo",
  "originCountry",
  "originState",
  "originCity",
  "originDepo",
  "pingStatus",
  "fleetName",
  "fleetCategory",
];

/**
 * Attributes a request must always ask for, whether or not a column shows them.
 *
 * pingStatus is not a column anywhere: it is a hidden attribute the device-id
 * renderer reads to colour the connectivity dot
 * (Renderers/columnTypes/DeviceIDLink.jsx). Deriving purely from the configured
 * columns would stop requesting it - no org configures a pingStatus column - and
 * every dot would render offline.
 */
export const ALWAYS_REQUESTED_META_INFO_FIELDS = ["pingStatus"];

/** The field name a column reads its value from. */
const fieldOf = (column) => column?.dataIndex || column?.key;

/**
 * The metaInfo fields a request should ask for, given the columns on show.
 *
 * `columns` accepts either the built column objects or a raw columnsConfig map.
 * Order follows `supportedFields` so the same column set always produces the
 * same request, which keeps it cacheable and makes a diff readable.
 */
export const deriveMetaInfoFields = (columns, supportedFields = []) => {
  // Object.entries, not Object.values: getColumnsConfig falls back to the map key
  // when an entry has no key or dataIndex, so a column can be named only by its
  // key. Reading the value alone would render a column nothing requested.
  const entries = Array.isArray(columns)
    ? columns.map((column) => [undefined, column])
    : Object.entries(columns || {});
  const wanted = new Set(
    entries.map(([mapKey, column]) => fieldOf(column) || mapKey).filter(Boolean)
  );
  ALWAYS_REQUESTED_META_INFO_FIELDS.forEach((field) => wanted.add(field));
  return supportedFields.filter((field) => wanted.has(field));
};

/**
 * Copy the requested entity attributes off an API row.
 *
 * Only the fields that were asked for are copied, so a row never gains a key
 * for a column the table is not showing.
 */
export const pickMetaInfoValues = (apiRow, fields = [], fallback = "—") => {
  const source = apiRow?.metaInfo || {};
  return fields.reduce((row, field) => {
    const value = source[field];
    // pingStatus is a status string the renderer interprets, so an absent value
    // must stay absent rather than become the em dash a text column wants.
    row[field] = value === undefined || value === null
      ? (field === "pingStatus" ? undefined : fallback)
      : value;
    return row;
  }, {});
};

/**
 * The fields a request should ask for: what it already asks for, plus the entity
 * attributes its configured columns need.
 *
 * A union rather than a replacement, because some requests ask through metaInfo
 * for things that are not entity attributes at all - `vehicleCount` on the BMS
 * level-one tables is a count. Deriving alone would drop those. Adding to them
 * can only ever request more, so a table that is not configured behaves exactly
 * as it did.
 */
export const withMetaInfoFields = (existingFields, columns, supportedFields = []) => {
  const derived = deriveMetaInfoFields(columns, supportedFields);
  const merged = [...(existingFields || [])];
  derived.forEach((field) => {
    if (!merged.includes(field)) merged.push(field);
  });
  return merged;
};
